/**
 * Catalog Store — units, market listings, project metadata.
 * In dev, reads from local JSON. In production, fetches from /api/units (Cloudflare Worker → Sheet).
 */

import { create } from 'zustand';
import unitsLocal from '@data/units.json';
import marketLocal from '@data/market.json';
import type { Unit, MarketListing } from '@lib/types';

interface CatalogState {
  units: Unit[];
  market: MarketListing[];
  /** Candidates produced by Step 3 (Goal) — consumed by Step 4 (Match). */
  filteredUnits: Unit[];
  loading: boolean;
  lastFetch: number | null;
  error: string | null;
  loadCatalog: () => Promise<void>;
  setFilteredUnits: (units: Unit[]) => void;
  filterUnits: (filters: {
    type?: Unit['type'];
    maxPrice?: number;
    minPrice?: number;
    status?: Unit['status'];
  }) => Unit[];
}

// Phase 8f-bcs (2026-05-07) — units.json migrated from a raw array
// (v1) to a versioned envelope { version, lastUpdated, source, units }.
// Read .units off the wrapper for both the initial load and the
// Worker-fallback case below.
const _unitsLocalArray = (unitsLocal as { units: unknown }).units as Unit[];

export const useCatalogStore = create<CatalogState>((set, get) => ({
  units: _unitsLocalArray,
  market: marketLocal as MarketListing[],
  filteredUnits: [],
  loading: false,
  lastFetch: null,
  error: null,

  setFilteredUnits: (filteredUnits) => set({ filteredUnits }),

  loadCatalog: async () => {
    set({ loading: true, error: null });
    try {
      // Try the Worker; fall back to local data if unreachable (offline tolerance)
      const res = await fetch('/api/units', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        set({
          units: (data.units as Unit[]) || _unitsLocalArray,
          loading: false,
          lastFetch: Date.now(),
        });
      } else {
        // Fallback to local
        set({ loading: false, lastFetch: Date.now() });
      }
    } catch (e) {
      // Offline / Worker not running — use local data
      console.warn('Catalog fetch failed, using local data:', e);
      set({ loading: false, error: null, lastFetch: Date.now() });
    }
  },

  filterUnits: (filters) => {
    const all = get().units;
    return all.filter((u) => {
      if (filters.type && u.type !== filters.type) return false;
      if (filters.status && u.status !== filters.status) return false;
      if (filters.maxPrice && u.basePrice > filters.maxPrice) return false;
      if (filters.minPrice && u.basePrice < filters.minPrice) return false;
      return true;
    });
  },
}));
